import Image from 'next/image';
import { useIntl } from 'react-intl';

import { Flex } from '@/components/Flex';
import { Typography } from '@/components/Typography';
import type { OrderAppCtaType } from '@/graphql/orderApp/api';
import type { MoneyFragment } from '@/graphql/saleor/api';
import type { Maybe } from '@/lib/types';
import {
  commonMessages,
  OneyDownPaymentMessage,
  PaymentCost,
} from '@/messages/common';
import { useConfigProvider } from '@/providers/ConfigProvider';
import { useRegionProvider } from '@/providers/RegionProvider';

import { ReactComponent as InfoBadge } from '@/assets/icons/info-badge-grey.svg';
import OneyIcon from '@/assets/icons/oney.svg';

import * as S from '../ProductDetail.styles';

interface OneySentenceProps {
  onOneyClick: () => void;
  price: Maybe<MoneyFragment>;
  variantCTA?: OrderAppCtaType;
}

export const OneySentence = ({
  onOneyClick,
  price,
  variantCTA,
}: OneySentenceProps) => {
  const { formatMessage } = useIntl();
  const { formatPrice } = useRegionProvider();
  const {
    oney3xPaymentMax,
    oney3xPaymentMin,
    oney4xPaymentMax,
    oney4xPaymentMin,
  } = useConfigProvider();

  if (!price || variantCTA !== 'ORDER') return null;

  const is4xAvailable =
    oney4xPaymentMin > 0 &&
    oney4xPaymentMax > 0 &&
    price.amount >= oney4xPaymentMin &&
    price.amount <= oney4xPaymentMax;

  const is3xAvailable =
    oney3xPaymentMin > 0 &&
    oney3xPaymentMax > 0 &&
    price.amount >= oney3xPaymentMin &&
    price.amount <= oney3xPaymentMax;

  const availableInstallments: number[] = [];
  if (is3xAvailable) availableInstallments.push(3);
  if (is4xAvailable) availableInstallments.push(4);

  if (availableInstallments.length === 0) return null;

  return (
    <S.OneySentence data-testid='oneySentence' onClick={onOneyClick}>
      <Flex alignItems='center' gridGap='s050'>
        <Image alt='oney' height={16} src={OneyIcon} width={50} />
        <Typography>{formatMessage(commonMessages.payIn)}</Typography>
        {availableInstallments.map((installment) => (
          <S.Oney3xBadge key={installment}>{installment}x</S.Oney3xBadge>
        ))}
      </Flex>
      <Typography>
        {OneyDownPaymentMessage(
          formatPrice(
            {
              amount:
                Math.ceil((price.amount / availableInstallments[0]) * 100) /
                100,
              currency: price.currency,
            },
            { currencyDisplay: 'symbol' },
          ),
        )}
      </Typography>
      <Typography>
        {PaymentCost(
          formatPrice(
            {
              amount: 0,
              currency: price.currency,
            },
            { currencyDisplay: 'symbol' },
          ),
        )}
        <InfoBadge height={12} width={12} />
      </Typography>
    </S.OneySentence>
  );
};
